/**
 * Leaderboards (docs/GAME_DESIGN.md § 8): Rated / Arcade tabs, scope chips
 * (Global → Grade), an optional grade filter, all-time / this week, a top-3
 * podium, the ranked list and the player's own row pinned at the bottom.
 * Clearly labelled when the offline demo backend (fictional players) is in use.
 */
import { useEffect, useState } from 'react';
import type { Locale } from '@dropstack/core';
import type { BackendClient, LeaderboardEntry, LeaderboardMetric, LeaderboardPage, LeaderboardPeriod, LeaderboardScope, PlayerProfile } from '../contracts.ts';
import { useT } from '../i18n/index.ts';
import { provinceName } from '../services/geo.ts';
import { Button } from '../components/Button.tsx';
import { ChoiceGroup } from '../components/Controls.tsx';
import { Icon } from '../components/Icon.tsx';
import { ScreenLayout } from '../components/ScreenLayout.tsx';
import { Spinner } from '../components/Spinner.tsx';
import { formatInteger } from './model/format.ts';
import { GRADES } from './model/onboarding.ts';
import {
  DEFAULT_BOARD_UI,
  LEADERBOARD_SCOPES,
  SCOPE_LABEL_KEYS,
  buildBoardRequest,
  isOnBoards,
  pinnedRow,
  podiumStep,
  scopePlaceName,
  splitPodium,
  type BoardUiState,
} from './model/leaderboard.ts';
import './leaderboard.css';

export interface LeaderboardScreenProps {
  /** Null while the backend is still being chosen. */
  backend: BackendClient | null;
  profile: PlayerProfile | null;
  locale: Locale;
  onBack(): void;
  onJoin(): void;
}

type Load =
  | { status: 'loading' }
  | { status: 'ready'; page: LeaderboardPage }
  | { status: 'error' }
  | { status: 'unavailable'; reason: 'needs-profile' | 'needs-grade' };

const METRIC_NOTE_KEYS: Record<LeaderboardMetric, string> = {
  rated: 'shell.lbRatedNote',
  raw: 'shell.lbArcadeNote',
};

export function LeaderboardScreen({ backend, profile, locale, onBack, onJoin }: LeaderboardScreenProps) {
  const t = useT();
  const [ui, setUi] = useState<BoardUiState>(DEFAULT_BOARD_UI);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const update = (patch: Partial<BoardUiState>) => setUi((current) => ({ ...current, ...patch }));

  useEffect(() => {
    const request = buildBoardRequest(ui, profile);
    if (!request.ok) {
      setLoad({ status: 'unavailable', reason: request.reason });
      return;
    }
    if (!backend) {
      setLoad({ status: 'loading' });
      return;
    }
    let live = true;
    setLoad({ status: 'loading' });
    backend.getLeaderboard(request.query).then(
      (page) => live && setLoad({ status: 'ready', page }),
      () => live && setLoad({ status: 'error' }),
    );
    return () => {
      live = false;
    };
  }, [backend, profile, ui, attempt]);

  const scopeLabel = (scope: LeaderboardScope): string => scopePlaceName(scope, profile, locale) ?? t(SCOPE_LABEL_KEYS[scope]);

  return (
    <ScreenLayout title={t('shell.lbTitle')} onBack={onBack} backLabel={t('common.back')} className="leaderboard">
      {backend?.kind === 'offline-demo' && (
        <p className="leaderboard__demo">
          <span className="ui-badge">
            <Icon name="wifi-off" />
            {t('shell.lbDemoBadge')}
          </span>
          <span>{t('shell.lbDemoNote')}</span>
        </p>
      )}

      <div className="ui-panel leaderboard__controls">
        <ChoiceGroup<LeaderboardMetric>
          legend={t('shell.lbMetric')}
          hideLegend
          value={ui.metric}
          onChange={(metric) => update({ metric })}
          choices={[
            { value: 'rated', label: t('shell.lbRated') },
            { value: 'raw', label: t('shell.lbArcade') },
          ]}
          className="leaderboard__tabs"
        />
        <p className="leaderboard__metric-note">{t(METRIC_NOTE_KEYS[ui.metric])}</p>
        <ChoiceGroup<LeaderboardScope>
          legend={t('shell.lbScope')}
          variant="chips"
          value={ui.scope}
          onChange={(scope) => update({ scope })}
          choices={LEADERBOARD_SCOPES.map((scope) => ({ value: scope, label: scopeLabel(scope) }))}
        />
        <div className="leaderboard__filters">
          <label className="leaderboard__grade">
            <span>{t('shell.lbGradeFilter')}</span>
            <select
              className="ui-select"
              value={ui.grade ?? ''}
              onChange={(e) => update({ grade: e.currentTarget.value === '' ? null : Number(e.currentTarget.value) })}
            >
              <option value="">{ui.scope === 'grade' ? t('shell.lbMyGrade') : t('shell.lbAllGrades')}</option>
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {t('common.gradeN', { grade: g })}
                </option>
              ))}
            </select>
          </label>
          <ChoiceGroup<LeaderboardPeriod>
            legend={t('shell.lbPeriod')}
            hideLegend
            value={ui.period}
            onChange={(period) => update({ period })}
            choices={[
              { value: 'all', label: t('shell.lbAllTime') },
              { value: 'week', label: t('shell.lbThisWeek') },
            ]}
          />
        </div>
      </div>

      <Board load={load} locale={locale} onRetry={() => setAttempt((n) => n + 1)} onJoin={onJoin} />

      {!isOnBoards(profile) && (
        <div className="ui-panel leaderboard__join">
          <p>{t('shell.lbNotOnBoards')}</p>
          <Button variant="primary" icon="trophy" onClick={onJoin}>
            {t('shell.goJoinButton')}
          </Button>
        </div>
      )}
    </ScreenLayout>
  );
}

function Board({ load, locale, onRetry, onJoin }: { load: Load; locale: Locale; onRetry(): void; onJoin(): void }) {
  const t = useT();
  switch (load.status) {
    case 'loading':
      return (
        <div className="ui-panel leaderboard__state" aria-busy="true">
          <Spinner label={t('shell.lbLoading')} />
        </div>
      );
    case 'error':
      return (
        <div className="ui-panel leaderboard__state">
          <p>{t('shell.lbError')}</p>
          <Button icon="refresh" onClick={onRetry}>
            {t('common.tryAgain')}
          </Button>
        </div>
      );
    case 'unavailable':
      return load.reason === 'needs-grade' ? (
        <div className="ui-panel leaderboard__state">
          <p>{t('shell.lbNeedsGrade')}</p>
        </div>
      ) : (
        <div className="ui-panel leaderboard__state">
          <p>{t('shell.lbNeedsProfile')}</p>
          <Button variant="primary" icon="trophy" onClick={onJoin}>
            {t('shell.goJoinButton')}
          </Button>
        </div>
      );
    case 'ready':
      return <BoardPage page={load.page} locale={locale} />;
  }
}

function BoardPage({ page, locale }: { page: LeaderboardPage; locale: Locale }) {
  const t = useT();
  if (page.entries.length === 0) {
    return (
      <div className="ui-panel leaderboard__state">
        <Icon name="trophy" size="2.5rem" />
        <p>{t('shell.lbEmpty')}</p>
      </div>
    );
  }
  const { podium, rest } = splitPodium(page.entries);
  const me = pinnedRow(page);
  return (
    <div className="leaderboard__board">
      <p className="leaderboard__total">{t('shell.lbTotal', { count: formatInteger(page.total) })}</p>
      <ol className="leaderboard__podium" aria-label={t('shell.lbPodium')}>
        {podium.map((entry) => (
          <li key={entry.entryKey} className={`leaderboard__step leaderboard__step--${podiumStep(entry)}${entry.isMe ? ' is-me' : ''}`}>
            <span className="leaderboard__medal" aria-hidden="true">
              {entry.rank}
            </span>
            <span className="visually-hidden">{t('shell.lbRankN', { rank: entry.rank })}</span>
            <span className="leaderboard__step-name">{entry.displayName}</span>
            <span className="leaderboard__step-score">{formatInteger(entry.score)}</span>
            <span className="leaderboard__step-detail">{rowDetail(entry, locale, t)}</span>
            <span className="leaderboard__step-block" aria-hidden="true" />
          </li>
        ))}
      </ol>
      {rest.length > 0 && (
        <ol className="leaderboard__list" start={podium.length + 1} aria-label={t('shell.lbList')}>
          {rest.map((entry) => (
            <Row key={entry.entryKey} entry={entry} locale={locale} />
          ))}
        </ol>
      )}
      {me && (
        <div className="leaderboard__me">
          <span className="leaderboard__me-label">{t('shell.lbYou')}</span>
          <Row entry={me} locale={locale} as="div" />
        </div>
      )}
    </div>
  );
}

function Row({ entry, locale, as = 'li' }: { entry: LeaderboardEntry; locale: Locale; as?: 'li' | 'div' }) {
  const t = useT();
  const Tag = as;
  return (
    <Tag className={entry.isMe ? 'leaderboard__row is-me' : 'leaderboard__row'}>
      <span className="leaderboard__rank">
        <span className="visually-hidden">{t('shell.lbRankN', { rank: entry.rank })}</span>
        <span aria-hidden="true">{formatInteger(entry.rank)}</span>
      </span>
      <span className="leaderboard__who">
        <span className="leaderboard__name">{entry.displayName}</span>
        <span className="leaderboard__detail">{rowDetail(entry, locale, t)}</span>
      </span>
      <span className="leaderboard__level">{t('common.levelN', { level: entry.levelReached })}</span>
      <span className="leaderboard__score">{formatInteger(entry.score)}</span>
    </Tag>
  );
}

/** "Grade 7 · Baobab High School" (falls back to the town, then the province). */
function rowDetail(entry: LeaderboardEntry, locale: Locale, t: ReturnType<typeof useT>): string {
  const place = entry.schoolName || entry.city || provinceName(entry.province, locale);
  const grade = t('common.gradeN', { grade: entry.grade });
  return place ? `${grade} · ${place}` : grade;
}
